import { useMemo, useState } from 'react';
import { Plus, Star, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { useEmployees } from '@/features/hrm/employees/queries';
import { employeeName } from '@/features/hrm/employees/types';
import {
  useCreateEmployeeSkill,
  useCreateSkill,
  useDeleteSkill,
  useEmployeeSkills,
  useSkills,
  useUpdateEmployeeSkill,
} from '../queries';
import type { Skill } from '../api';

/** Compact "1-5" cell — clickable level chips. */
function LevelCell({
  current,
  onSelect,
}: {
  current: number;
  onSelect: (level: number) => void;
}) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onSelect(n)}
          aria-label={`Set level ${n}`}
          className={cn(
            'size-4 rounded-sm transition-colors',
            n <= current ? 'bg-primary hover:bg-primary/80' : 'bg-muted hover:bg-muted-foreground/20',
          )}
        />
      ))}
    </div>
  );
}

export function SkillsMatrixPage() {
  const { data: skillsData, isPending: skillsPending } = useSkills();
  const { data: employeesData, isPending: employeesPending } = useEmployees({});
  const { data: matrixData } = useEmployeeSkills();
  const create = useCreateEmployeeSkill();
  const update = useUpdateEmployeeSkill();
  const removeSkill = useDeleteSkill();
  const addSkill = useCreateSkill();

  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillCategory, setNewSkillCategory] = useState('');
  const [toDelete, setToDelete] = useState<Skill | null>(null);

  const skills = skillsData?.results ?? [];
  const employees = employeesData?.results ?? [];

  /** Lookup: `${employeeId}:${skillId}` → EmployeeSkill row (level + id). */
  const grid = useMemo(() => {
    const map = new Map<string, { id: number; level: number }>();
    for (const row of matrixData?.results ?? []) {
      map.set(`${row.employee}:${row.skill}`, { id: row.id, level: row.level });
    }
    return map;
  }, [matrixData]);

  function setLevel(employeeId: number, skill: Skill, level: number) {
    const key = `${employeeId}:${skill.id}`;
    const existing = grid.get(key);
    if (existing) {
      if (existing.level === level) return;
      update.mutate({ id: existing.id, input: { level } });
    } else {
      create.mutate({ employee: employeeId, skill: skill.id, level });
    }
  }

  async function submitNewSkill(e: React.FormEvent) {
    e.preventDefault();
    if (!newSkillName.trim()) return;
    await addSkill.mutateAsync({ name: newSkillName.trim(), category: newSkillCategory.trim() });
    setNewSkillName('');
    setNewSkillCategory('');
    setSkillDialogOpen(false);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Skills Matrix</h1>
          <p className="text-muted-foreground text-sm">
            Per-employee proficiency on each skill (1–5). Click a square to set the level.
          </p>
        </div>
        <Button onClick={() => setSkillDialogOpen(true)}>
          <Plus className="size-4" /> Add skill
        </Button>
      </div>

      {skillsPending || employeesPending ? (
        <Skeleton className="h-72 w-full" />
      ) : skills.length === 0 ? (
        <div className="text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-sm">
          <Star className="size-6" />
          No skills defined. Add one above to start scoring.
        </div>
      ) : employees.length === 0 ? (
        <div className="text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-sm">
          No employees to score yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 z-10 bg-background">Employee</TableHead>
                {skills.map((s) => (
                  <TableHead key={s.id} className="min-w-[120px]">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{s.name}</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Remove skill ${s.name}`}
                        className="size-5 opacity-50 hover:opacity-100"
                        onClick={() => setToDelete(s)}
                      >
                        <Trash2 className="size-3" />
                      </Button>
                    </div>
                    {s.category ? (
                      <Badge variant="outline" className="mt-1 text-[10px]">
                        {s.category}
                      </Badge>
                    ) : null}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="sticky left-0 z-10 bg-background font-medium">
                    {employeeName(e)}
                  </TableCell>
                  {skills.map((s) => {
                    const cur = grid.get(`${e.id}:${s.id}`)?.level ?? 0;
                    return (
                      <TableCell key={s.id}>
                        <LevelCell current={cur} onSelect={(level) => setLevel(e.id, s, level)} />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={skillDialogOpen} onOpenChange={setSkillDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add skill</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitNewSkill} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="new-skill-name">
                Name
              </label>
              <Input
                id="new-skill-name"
                value={newSkillName}
                onChange={(ev) => setNewSkillName(ev.target.value)}
                placeholder="React, Python, Sales…"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="new-skill-cat">
                Category
              </label>
              <Input
                id="new-skill-cat"
                value={newSkillCategory}
                onChange={(ev) => setNewSkillCategory(ev.target.value)}
                placeholder="Engineering, Sales…"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSkillDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!newSkillName.trim() || addSkill.isPending}>
                {addSkill.isPending ? 'Saving…' : 'Add skill'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove skill?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `"${toDelete.name}" and all per-employee scores for it will be removed.` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) removeSkill.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
